import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, User, Lock, ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
// import Header from '@/components/Header'; // 已注释
// import Footer from '@/components/Footer'; // 已注释
import { captchaApi, userApi, storage } from '@/user/api';
import type { CaptchaVO } from '@/types/api-entity';

// 1. 导入背景图片
import loginBg from '../assest/imgs/loginBg.jpg'; 
// 2. 导入 Logo 图片
import logoImg from '../assest/imgs/kmzs-logo-mini.png'; 

export default function Login() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [captchaInfo, setCaptchaInfo] = useState<CaptchaVO | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  // 加载验证码
  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const result = await captchaApi.getCaptcha();
      if (result.code === 200) {
        setCaptchaInfo(result.data);
      }
    } catch (err) {
      setError('加载验证码失败，请刷新页面重试');
    } finally {
      setCaptchaLoading(false);
    }
  };

  // 页面加载时获取验证码
  useEffect(() => {
    loadCaptcha();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!captchaInfo) {
      setError('验证码加载失败，请刷新页面');
      return;
    }

    setLoading(true);
    
    try {
      const result = await userApi.login({
        username,
        password,
        captcha,
        captchaKey: captchaInfo.captchaKey,
      });
      
      if (result.code === 200) {
        navigate('/');
      } else {
        setError(result.message || '登录失败');
        loadCaptcha();
        setCaptcha('');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="w-full h-screen flex flex-col relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBg})` }}
    >
      {/* 标语区域 */}
      <div className="absolute inset-0 flex flex-col items-start justify-center pl-20 md:pl-32 z-0 pointer-events-none">
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg tracking-wide">
          一站式建材工序生态
        </h1>
        <p className="text-xl md:text-2xl text-white/90 font-medium drop-shadow-md tracking-wider">
          汇聚货源 · 高效集采 · 轻松入驻
        </p>
      </div>
      
      {/* main 区域 (登录框) */}
      <main className="flex-1 flex items-center justify-end py-12 px-40 relative z-10">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-white/20">
            
            {/* Header 改为横向布局 */}
            <div className="flex items-center justify-center gap-3 mb-8">
              {/* Logo 容器 */}
              <div className="flex items-center">
                <img src={logoImg} alt="Logo" className="w-40 h-24 object-contain" />
              </div>
              
              {/* 标题文本 */}
              <div className="text-left">
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">授权登录</h1>
                <p className="text-sm text-gray-500 mt-1">登录您的互联网生态平台账户</p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm animate-pulse">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-700">用户名</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="请输入用户名"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">密码</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="请输入密码"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Captcha */}
              <div className="space-y-2">
                <Label htmlFor="captcha" className="text-gray-700">验证码</Label>
                <div className="flex gap-3">
                  <Input
                    id="captcha"
                    type="text"
                    placeholder="请输入验证码"
                    value={captcha}
                    onChange={(e) => setCaptcha(e.target.value)}
                    className="h-12 flex-1 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                    required
                    maxLength={4}
                  />
                  <div 
                    className="w-32 h-12 bg-gray-100 rounded-lg overflow-hidden cursor-pointer flex items-center justify-center border border-gray-200 hover:border-blue-400 transition-colors"
                    onClick={loadCaptcha}
                    title="点击刷新验证码"
                  >
                    {captchaLoading ? (
                      <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                    ) : captchaInfo ? (
                      <img 
                        src={captchaInfo.captchaImage} 
                        alt="验证码" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">加载中</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  />
                  <Label htmlFor="remember" className="text-sm text-gray-500 cursor-pointer">
                    记住我
                  </Label>
                </div>
                <Link to="#" className="text-sm text-[#1c5bff] hover:underline font-medium">
                  忘记密码？
                </Link>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full h-12 bg-[#1c5bff] text-white hover:bg-[#1547cc] font-semibold shadow-lg shadow-blue-500/30 transition-all duration-300 transform hover:-translate-y-0.5"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> 登录中...
                  </span>
                ) : (
                  <>
                    登录 <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Register Link */}
            <p className="text-center mt-8 text-gray-500">
              还没有账户？{' '}
              <Link to="/register" className="text-[#1c5bff] font-semibold hover:underline">
                立即注册
              </Link>
            </p>
          </div>
          
          {/* 注意：原有的底部版权信息已从卡片内移除 */}
        </div>
      </main>

      {/* 
         新增：固定在屏幕底部的版权信息 
         - absolute bottom-4: 距离底部 1rem (16px)
         - left-0 right-0: 左右撑开
         - text-center: 文字居中
         - z-20: 确保在最上层
         - pointer-events-none: 防止遮挡背景点击（如果需要点击背景的话），但这里主要是展示
         - text-white/80: 白色半透明文字，适配深色或浅色背景
      */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-20 pointer-events-none">
        <p className="text-white/80 text-sm drop-shadow-md">
          &copy; 2024 孔明平台. All rights reserved.
        </p>
      </div>

    </div>
  );
}